// "Location Assignment" change panel — assigns an interested party
// (mortgagee/loss payee) to a risk location, with an optional nature-of-
// interest override for that assignment.
//
// Mirrors AutomobilePolicy V2's DriverAssignmentChangeV2.tsx (vehicle +
// driver + Principal/Occasional + percentage), adapted to Habitational (no
// vehicles/drivers here — risk locations and interested parties are the
// comparable entities).
//
// Rendered in the v1-style RightPanel (blue header + Close button) with an
// Add/Change checkbox toggle at the top:
//   - Add mode    -> create a brand new assignment record.
//   - Change mode -> pick an existing assignment (by interested party) to
//                    replace, then enter the new assignment's details,
//                    mirroring the risk-location/coverage/schedule/
//                    interested-party substitution pattern.
//
// Both the Risk Location and Interested Party dropdowns are restricted to
// ACTIVE records only (filtered client-side, matching
// AddressChangeInformationV2's approach so the shared hooks' behaviour for
// other consumers stays unchanged).

import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import produce from "immer";
import { RightPanel } from "../../../../../../../../components/RightPanel/RightPanel";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../../../components/formsComponents";
import { useHabitationalPolicyRiskLocation } from "../../../hooks/useHabitationalPolicyRiskLocation";
import { useHabitationalIntrestedParty } from "../../../hooks/useHabitationalIntrestedParty";
import { IHabitationalPolicyChange } from "../../../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { IHabitationalRiskLocation } from "../../../../../../../../redux/habitationalPolicy/habitationalRiskLocation/habitationalRiskLocation.types";
import { IHabitationalInterested } from "../../../../../../../../redux/habitationalPolicy/habitationalInterestedParty/habitationalInterested.types";

interface ILocationAssignmentChangeV2Props {
  open: boolean;
  // "Add" | "Change" chosen on the board.
  mode?: string | null;
  values: IHabitationalPolicyChange;
  setValues: (values: any, shouldValidate?: boolean) => any;
  customerPolicyId?: number;
  autoSaveData?: (values: IHabitationalPolicyChange) => void;
  onClose: () => void;
  isStatusCompleted?: boolean;
  // When set, edits the existing record at this index (from "Edit" in
  // Changes Requested) instead of creating a new one.
  editIndex?: number;
}

export const LocationAssignmentChangeV2: React.FC<
  ILocationAssignmentChangeV2Props
> = ({
  open,
  mode,
  values,
  setValues,
  customerPolicyId,
  autoSaveData = () => {},
  onClose,
  isStatusCompleted,
  editIndex,
}) => {
  const { riskLocations } = useHabitationalPolicyRiskLocation(customerPolicyId);
  const { interestedPartyList } = useHabitationalIntrestedParty(customerPolicyId);

  // Active-only, filtered client-side (the shared hooks don't filter by
  // status themselves — matching AddressChangeInformationV2's approach so
  // other consumers of these hooks are unaffected).
  const activeRiskLocationDropdown = React.useMemo(
    () =>
      riskLocations
        .filter(
          (location: IHabitationalRiskLocation) => location.status === "ACTIVE"
        )
        .map((location: IHabitationalRiskLocation) => ({
          label: `${location.incremental_number ?? "--"} (${
            location.address || "N/A"
          }, ${location.city || ""})`,
          value: location.incremental_number ?? "",
        })),
    [riskLocations]
  );

  const activeInterestedPartyDropdown = React.useMemo(
    () =>
      interestedPartyList
        .filter((party: IHabitationalInterested) => party.status === "ACTIVE")
        .map((party: IHabitationalInterested) => ({
          label: `${party.incremental_number ?? "--"} (${
            party.mortgage_or_loss_payee_name || "N/A"
          })`,
          value: party.incremental_number ?? "",
        })),
    [interestedPartyList]
  );

  const assignmentChange = values.location_assignment_change;
  const isEdit = editIndex !== undefined;
  const existing = isEdit
    ? assignmentChange?.records?.[editIndex as number]
    : undefined;
  const effectiveMode = mode || assignmentChange?.type || "Add";
  const isChangeMode = effectiveMode === "Change";

  // Local draft for the single record being added/substituted/edited.
  // Committed to values.location_assignment_change.records on "Add to
  // request". mode/delete_location_assignment are carried through unchanged
  // when editing (the Add/Change picker is hidden in edit mode) and
  // re-derived from the panel's current mode selection when creating a new
  // record.
  const [draft, setDraft] = React.useState<{
    risk_location_number: number | null;
    interested_party_number: number | null;
    nature_of_interest: string | null;
    notes: string | null;
    mode?: "Add" | "Change" | null;
    delete_location_assignment?: string | null;
  }>(
    existing
      ? { ...existing }
      : {
          risk_location_number: null,
          interested_party_number: null,
          nature_of_interest: null,
          notes: null,
          mode: null,
          delete_location_assignment: null,
        }
  );

  // Which existing assignment (by interested_party_number) is being
  // replaced, only relevant in Change mode.
  const [substitutionTarget, setSubstitutionTarget] = React.useState<
    string | number
  >(assignmentChange?.delete_location_assignment || "");

  const handleModeChange = (nextMode: "Add" | "Change") => {
    setValues(
      produce(values, (draftValues) => {
        if (!draftValues.location_assignment_change) {
          draftValues.location_assignment_change = {
            type: null,
            delete_location_assignment: null,
            records: [],
          };
        }
        draftValues.location_assignment_change.type = nextMode;
      })
    );
  };

  const handleSave = () => {
    const newValues = produce(values, (draftValues) => {
      if (!draftValues.location_assignment_change) {
        draftValues.location_assignment_change = {
          type: null,
          delete_location_assignment: null,
          records: [],
        };
      }
      // The mode is stamped onto the record itself (not the shared section
      // type) so it stays fixed for that record even if a later Add/Change
      // click on the board flips the section-level mode for the NEXT record.
      const recordToSave = {
        ...draft,
        mode: isEdit ? draft.mode : isChangeMode ? "Change" : "Add",
        delete_location_assignment: isEdit
          ? draft.delete_location_assignment
          : isChangeMode
          ? String(substitutionTarget)
          : null,
      };
      if (isEdit) {
        draftValues.location_assignment_change.records[
          editIndex as number
        ] = recordToSave;
      } else {
        draftValues.location_assignment_change.records.push(recordToSave);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    onClose();
  };

  const canSave =
    !!draft.risk_location_number &&
    !!draft.interested_party_number &&
    (!isChangeMode || !!substitutionTarget);

  return (
    <RightPanel
      open={open}
      heading="Location Assignment Change"
      onClose={onClose}
      actionButtons={() => (
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          <Button
            variant="contained"
            disabled={!canSave || isStatusCompleted}
            onClick={handleSave}
          >
            {isEdit ? "Save" : "Add to request"}
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      )}
    >
      {!isEdit && (
        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center">
            <Checkbox
              checked={!isChangeMode}
              onChange={(e) => {
                if (e.target.checked) {
                  handleModeChange("Add");
                }
              }}
            />
            <Typography>Add</Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Checkbox
              checked={isChangeMode}
              onChange={(e) => {
                if (e.target.checked) {
                  handleModeChange("Change");
                }
              }}
            />
            <Typography>Change</Typography>
          </Stack>
        </Stack>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assigns an interested party (mortgagee/loss payee) to a risk
        location.
      </Typography>

      {isChangeMode && !isEdit && (
        <Box sx={{ mb: 2 }}>
          <CustomFormLabel>Assignment to Replace</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="substitution_target"
            value={substitutionTarget}
            onChange={(e: any) => setSubstitutionTarget(e.target.value)}
            placeholder="Select One"
            options={activeInterestedPartyDropdown}
          />
        </Box>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Risk Location</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="risk_location_number"
            value={draft.risk_location_number ?? ""}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                risk_location_number: e.target.value,
              }))
            }
            placeholder="Select One"
            options={activeRiskLocationDropdown}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Interested Party</CustomFormLabel>
          <ControlledCustomSelect
            variant="outlined"
            size="small"
            type="string"
            fullWidth
            displayEmpty
            name="interested_party_number"
            value={draft.interested_party_number ?? ""}
            onChange={(e: any) =>
              setDraft((prev) => ({
                ...prev,
                interested_party_number: e.target.value,
              }))
            }
            placeholder="Select One"
            options={activeInterestedPartyDropdown}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <CustomFormLabel>Nature of Interest</CustomFormLabel>
          <CustomTextField
            fullWidth
            placeholder="e.g. Certificate Holder"
            value={draft.nature_of_interest || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDraft((prev) => ({
                ...prev,
                nature_of_interest: e.target.value,
              }))
            }
          />
        </Grid>
      </Grid>

      <Box>
        <CustomFormLabel>Notes</CustomFormLabel>
        <CustomTextField
          fullWidth
          multiline
          rows={4}
          value={draft.notes || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setDraft((prev) => ({ ...prev, notes: e.target.value }))
          }
        />
      </Box>
    </RightPanel>
  );
};
